import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Eye, Save, Wand2 } from "lucide-react";
import { localizationClient } from "../../api/v2/localizationClient";
import {
  BindingsPreviewResponse,
  LanguageCatalogItem,
  RepositoryGroup,
  WorkspaceBinding,
  WorkspaceConfig,
} from "../../api/v2/types";
import { ApiError, parseApiError } from "../../api/http/errors";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import TreePickerDialog from "./TreePickerDialog";
import { useToast } from "../../components/ui/toast/use-toast";
import { ToastAction } from "../../components/ui/toast/toast";
import { conflictLabel, mapConflictsByPath } from "./conflicts";
import SectionState from "../shared/SectionState";
import HelpHoverCard from "../shared/HelpHoverCard";
import InlineHint from "../shared/InlineHint";
import { Spinner } from "../../components/ui/spinner";

interface BindingsTabProps {
  owner: string;
  repo: string;
  workspaceKey: string;
  onWorkspaceUpdated: (workspace: WorkspaceConfig) => void;
}

const BindingsTab = ({ owner, repo, workspaceKey, onWorkspaceUpdated }: BindingsTabProps) => {
  const { toast } = useToast();
  const [workspace, setWorkspace] = useState<WorkspaceConfig | null>(null);
  const [bindings, setBindings] = useState<WorkspaceBinding[]>([]);
  const [languages, setLanguages] = useState<LanguageCatalogItem[]>([]);
  const [groups, setGroups] = useState<RepositoryGroup[]>([]);
  const [preview, setPreview] = useState<BindingsPreviewResponse | null>(null);
  const [saveMode, setSaveMode] = useState<"replace" | "merge">("replace");
  const [search, setSearch] = useState("");
  const [errorText, setErrorText] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const [conflictByPath, setConflictByPath] = useState<Record<string, string[]>>({});

  const notifyApiError = useCallback((error: ApiError) => {
    toast({
      variant: "destructive",
      title: error.code,
      description: error.message,
      action: error.requestId ? (
        <ToastAction
          altText="Copy request id"
          onClick={() => navigator.clipboard?.writeText(error.requestId || "")}
        >
          <Copy className="h-4 w-4 mr-1" />
          {error.requestId}
        </ToastAction>
      ) : undefined,
    });
  }, [toast]);

  useEffect(() => {
    const load = async () => {
      setErrorText("");
      setIsBusy(true);
      try {
        const data = await localizationClient.getWorkspace(owner, repo, workspaceKey);
        const [catalog, repositoryGroups] = await Promise.all([
          localizationClient.getCatalogLanguages(),
          localizationClient.getRepositoryGroups(owner, repo),
        ]);
        setWorkspace(data);
        setBindings(data.languageBindings || []);
        setLanguages(catalog.languages);
        setGroups(repositoryGroups.groups);
      } catch (error) {
        const parsedError = parseApiError(error);
        setErrorText(parsedError.message);
        notifyApiError(parsedError);
      } finally {
        setIsBusy(false);
      }
    };
    load();
  }, [owner, repo, workspaceKey, notifyApiError]);

  const filteredBindings = useMemo(() => {
    const lower = search.trim().toLowerCase();
    if (!lower) return bindings;
    return bindings.filter((binding) =>
      `${binding.filePath} ${binding.language} ${binding.group}`.toLowerCase().includes(lower)
    );
  }, [bindings, search]);

  const runPreview = async () => {
    if (!bindings.length || isBusy) return;
    setErrorText("");
    setIsBusy(true);
    try {
      const response = await localizationClient.previewBindings(owner, repo, workspaceKey, bindings);
      setPreview(response);
      const conflicts = response.conflicts as Array<{
        reason: string;
        index?: number;
        key?: string;
        value?: string;
      }>;
      setConflictByPath(mapConflictsByPath(bindings, conflicts));
    } catch (error) {
      const parsedError = parseApiError(error);
      setErrorText(parsedError.message);
      notifyApiError(parsedError);
    } finally {
      setIsBusy(false);
    }
  };

  const saveBindings = async () => {
    setErrorText("");
    setIsBusy(true);
    try {
      const response = await localizationClient.saveBindings(owner, repo, {
        workspaceKey,
        mode: saveMode,
        bindings,
      });
      setWorkspace(response.workspace);
      setBindings(response.workspace.languageBindings);
      onWorkspaceUpdated(response.workspace);
      setConflictByPath({});
    } catch (error) {
      const parsedError = parseApiError(error);
      setErrorText(parsedError.message);
      notifyApiError(parsedError);
      const details = Array.isArray(parsedError.details) ? parsedError.details : [];
      setConflictByPath(mapConflictsByPath(bindings, details as Array<{ reason: string; index?: number }>));
    } finally {
      setIsBusy(false);
    }
  };

  const discoverBindings = async () => {
    setErrorText("");
    setIsBusy(true);
    try {
      const response = await localizationClient.discoverWorkspace(owner, repo, workspaceKey);
      setWorkspace(response);
      setBindings(response.languageBindings);
      onWorkspaceUpdated(response);
      setConflictByPath({});
    } catch (error) {
      const parsedError = parseApiError(error);
      setErrorText(parsedError.message);
      notifyApiError(parsedError);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <HelpHoverCard
          title="Bindings workflow"
          description="Pick locale files, assign language and group, run preview to validate conflicts, then save bindings to workspace."
        />
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <TreePickerDialog
          owner={owner}
          repo={repo}
          workspaceKey={workspaceKey}
          defaultBranch={workspace?.defaultBranch}
          selectedBindings={bindings}
          onChangeBindings={setBindings}
        />
        <Button variant="outline" onClick={discoverBindings} disabled={isBusy}>
          {isBusy ? <Spinner className="h-4 w-4 mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />} Discover
        </Button>
        <Button variant="outline" onClick={runPreview} disabled={isBusy || bindings.length === 0}>
          <Eye className="h-4 w-4 mr-1" /> Preview
        </Button>
        <InlineHint text="Preview validates duplicate paths, invalid values and missing metadata before save." />
        <Select value={saveMode} onValueChange={(value: "replace" | "merge") => setSaveMode(value)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="replace">replace</SelectItem>
            <SelectItem value="merge">merge</SelectItem>
          </SelectContent>
        </Select>
        <InlineHint text="Replace rewrites all bindings in workspace. Merge updates existing ones without removing others." />
        <Button onClick={saveBindings} disabled={isBusy || bindings.length === 0}>
          {isBusy ? <Spinner className="h-4 w-4 mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save bindings
        </Button>
      </div>

      <Input
        placeholder="Search by file path, language or group"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <SectionState
        isLoading={isBusy && bindings.length === 0}
        errorText={errorText}
        isEmpty={filteredBindings.length === 0}
        emptyText={bindings.length === 0 ? "No bindings yet. Open file picker or run discover." : "No bindings match current filter."}
        minHeightClassName="min-h-[260px]"
      >
      <div className="border rounded">
        <div className="grid grid-cols-3 gap-2 p-2 text-sm font-semibold border-b">
          <span className="flex items-center gap-1">File path <InlineHint text="Repository relative path to locale file." /></span>
          <span>Language</span>
          <span>Group</span>
        </div>
        {filteredBindings.map((binding) => (
          <div key={binding.filePath} className="grid grid-cols-3 gap-2 p-2 border-b last:border-b-0">
            <span className="text-xs break-all">{binding.filePath}</span>
            <Select
              value={binding.language}
              onValueChange={(value) =>
                setBindings((current) =>
                  current.map((item) =>
                    item.filePath === binding.filePath ? { ...item, language: value } : item
                  )
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                {languages.map((language) => (
                  <SelectItem key={language.code} value={language.code}>
                    {language.name} ({language.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={binding.group}
              onValueChange={(value) =>
                setBindings((current) =>
                  current.map((item) =>
                    item.filePath === binding.filePath ? { ...item, group: value } : item
                  )
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select group" />
              </SelectTrigger>
              <SelectContent>
                {groups.map((group) => (
                  <SelectItem key={group.groupKey} value={group.groupKey}>
                    {group.groupName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {conflictByPath[binding.filePath]?.length ? (
              <div className="col-span-3 text-xs text-red-500">
                {conflictByPath[binding.filePath].map(conflictLabel).join(", ")}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      </SectionState>
      <p className="text-xs text-muted-foreground">Showing {filteredBindings.length} of {bindings.length} bindings.</p>

      {preview ? (
        <div className="rounded border p-3 space-y-2">
          <p className="text-sm font-semibold">Preview conflicts: {preview.stats.conflicts}</p>
          {preview.conflicts.map((conflict) => (
            <p key={`${conflict.reason}-${conflict.key}-${conflict.index}`} className="text-xs text-red-500">
              {conflictLabel(conflict.reason)}: {conflict.key || conflict.value || `row ${conflict.index}`}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default BindingsTab;
